import styles from "./Pill.module.scss";
import { ComponentType, useEffect, useState } from "react";
import { IconCheckmark, IconClose, IconException, IconProps } from "../icons";
import classNames from "classnames";

export interface PillProps {
  /** The label to display in the pill. */
  label?: string;
  /** The visual type of the pill. */
  variant: "Default" | "Success" | "Error" | "Warning";
  /** The icon to display in the pill. */
  Icon?: ComponentType<IconProps>;
  /** If this pill has an associate action, define the type of action it performs */
  actionType?: "click" | "dismiss" | "dropdown" | "select";
  /** Is this pill disabled? */
  disabled?: boolean;
  /** Is this pill selected? */
  selected?: boolean;
  /** The action to perform when the pill's action is performed. */
  onAction?: (value?: unknown) => void;
  /** The value that is emitted when this pill's action is initiated. */
  value?: unknown;
  /** Optionally, set a fixed width for the pill. */
  width?: number | string;
  /** Optionally add custom styling. */
  className?: string;
}

export const Pill = (props: PillProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const pillContainerStyling = classNames(
    styles.pillContainer,
    props.actionType == "click" ? styles.statusPillClickable : "",
    isOpen ? styles.opened : "",
  );

  const pillStyling = classNames(
    styles.basePillStyling,
    props.className && props.className,
    props.actionType === "click" || props.actionType === "dropdown"
      ? styles.actionablePill
      : "",
    (props.actionType === "click" || props.actionType === "dropdown") &&
      props.selected
      ? styles.selectedPill
      : "",
    (props.actionType === "click" || props.actionType === "dropdown") &&
      props.disabled
      ? styles.disabledPill
      : "",
    props.actionType === "dismiss"
      ? styles.dismissPresent
      : styles.dismissNotPresent,
    props.actionType !== "click" ? styles[props.variant] : "",
  );

  const dismissibleIconStyling = classNames(
    styles.dismissibleIcon,
    styles[`background-${props.variant.toLowerCase()}`],
  );

  const iconToDisplay =
    "statusPill" in props ? (
      <IconException className={styles.mainIcon} />
    ) : "Icon" in props && props.Icon ? (
      <props.Icon className={styles.mainIcon} />
    ) : "selected" in props && props.selected ? (
      <IconCheckmark className={styles.mainIcon} />
    ) : (
      <></>
    );

  const [pillContent, setPillContent] = useState<JSX.Element | null>(null);

  const handleDismiss = () => {
    if (!props.disabled) {
      if (props.actionType === "dismiss" && props.onAction) {
        props.onAction(props.value);
      }

      setPillContent(null);
    }
  };

  const handleOnDropdownClick = () => {
    setIsOpen((prevVal: boolean) => !prevVal);
  };

  const dismissibleIcon =
    props.actionType === "dismiss" ? (
      <span
        tabIndex={props.disabled ? -1 : 0}
        role="button"
        onClick={handleDismiss}
        onKeyDown={
          props.disabled
            ? undefined
            : (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleDismiss();
                }
              }
        }
        className={`${dismissibleIconStyling} ${
          props.disabled ? styles.dismissDisabled : ""
        }`}
        aria-label="Dismiss"
        aria-disabled={props.disabled}
      >
        <IconClose />
      </span>
    ) : null;

  useEffect(() => {
    setPillContent(() => {
      const content = (
        <span
          className={pillContainerStyling}
          onClick={
            props.actionType === "click"
              ? () => {
                  if (props.onAction) props.onAction(props.value);
                }
              : props.actionType == "dropdown"
              ? handleOnDropdownClick
              : () => {}
          }
        >
          <div
            tabIndex={0}
            className={pillStyling}
            style={
              props.width
                ? {
                    width: props.width,
                    justifyContent: "space-between",
                  }
                : {}
            }
          >
            <span
              className={styles.pillInnerDisplay}
              data-testid="pill-inner-display"
            >
              {iconToDisplay}
              {props.label}
            </span>
            {dismissibleIcon}
          </div>
        </span>
      );

      if (props.actionType === "dropdown" && isOpen) {
        return <span className={styles.statusContentContainer}>{content}</span>;
      } else {
        return content;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props, isOpen]);

  return <>{pillContent}</>;
};
